import React, { Fragment } from 'react';

import { CloudProviders } from '@ariksa/cloud-account/api';
import { CloudProvider } from '@ariksa/inventory-core';
import { Box, Stack } from '@chakra-ui/react';
import map from 'lodash/map';
import toLower from 'lodash/toLower';

import { CustomTooltip } from 'components/DataDisplay';
import {
  AwsIcon,
  AzureIcon,
  BitbucketIcon,
  CustomIconTypes,
  GithubIcon,
  GitLabIcon,
  GoogleCloudIcon,
  OktaIcon,
  SnowflakeIcon,
  VmwareIcon,
} from 'components/Icons';
import { AriksaIcon, getIcon } from 'components/Icons/Components';

export const getCloudIcon = value => {
  let icon;
  switch (toLower(value)) {
    case 'aws':
      icon = <AwsIcon color="inherit" />;
      break;
    case 'azure':
      icon = <AzureIcon color="inherit" />;
      break;
    case 'vmware':
      icon = <VmwareIcon color="inherit" />;
      break;
    case 'gcp':
      icon = <GoogleCloudIcon color="inherit" />;
      break;
    case 'gitlab':
      icon = <GitLabIcon color="inherit" />;
      break;
    case 'gitlab_iac':
      icon = <GitLabIcon color="inherit" />;
      break;
  }

  return <AriksaIcon icon={icon} bg={value} />;
};

// Fixed: getCloudIcon always returns an element, so the raw-value fallback was
// unreachable; also key each icon.
export const renderCloudIcons = ({ value }) =>
  map(value, each => <Fragment key={each}>{getCloudIcon(each)}</Fragment>);

export const getProviderName = provider => {
  switch (provider) {
    case CloudProviders.Aws:
    case CloudProvider.Aws:
    case CustomIconTypes.AWS:
      return 'Amazon Web Services';
    case CloudProviders.Okta:
    case CustomIconTypes.Okta:
    case CloudProvider.Okta:
      return 'Okta';
    case CloudProviders.Azure:
    case CloudProvider.Azure:
      return 'Microsoft Azure';
    case CloudProviders.AzureAd:
    case CloudProvider.AzureAd:
    case 'azuread':
    case 'AzureAD':
      return 'Azure Active Directory';
    case CloudProviders.Snowflake:
    case CloudProvider.Snowflake:
    case CustomIconTypes.Snowflake:
      return 'Snowflake';
    case CloudProviders.Gcp:
    case CloudProvider.Gcp:
    case CustomIconTypes.GCP:
      return 'Google Cloud Platform';
    case CloudProvider.GoogleWorkspace:
    case CloudProviders.GoogleWorkspace:
      return 'Google Workspace';
    case CloudProviders.Gitlab:
    case CloudProvider.GitLab:
    case CustomIconTypes.GitLab:
      return 'GitLab';
    case CloudProviders.Github:
    case CloudProvider.GitHub:
    case CustomIconTypes.GitHub:
      return 'GitHub';
    case CloudProviders.Bitbucket:
    case CloudProvider.Bitbucket:
    case CustomIconTypes.BitBucket:
      return 'Atlassian BitBucket';
  }
};

export const getProviderAlias = provider => {
  switch (provider) {
    case CloudProviders.Aws:
    case CloudProvider.Aws:
    case CustomIconTypes.AWS:
      return 'AWS';
    case CloudProviders.Okta:
    case CloudProvider.Okta:
    case CustomIconTypes.Okta:
      return 'Okta';
    case CloudProviders.Azure:
    case CloudProvider.Azure:
      return 'Azure';
    case CloudProviders.AzureAd:
    case CloudProvider.AzureAd:
    case 'azuread':
      return 'AzureAD';
    case CloudProviders.Snowflake:
    case CloudProvider.Snowflake:
    case CustomIconTypes.Snowflake:
      return 'Snowflake';
    case CloudProviders.Gcp:
    case CloudProvider.Gcp:
      return 'GCP';
    case CloudProvider.GoogleWorkspace:
    case CloudProviders.GoogleWorkspace:
      return 'Google Workspace';
    case CloudProviders.Gitlab:
    case CloudProvider.GitLab:
    case CustomIconTypes.GitLab:
      return 'GitLab';
    case CloudProviders.Github:
    case CloudProvider.GitHub:
    case CustomIconTypes.GitHub:
      return 'GitHub';
    case CloudProviders.Bitbucket:
    case CloudProvider.Bitbucket:
    case CustomIconTypes.BitBucket:
      return 'BitBucket';
    default:
      return provider;
  }
};

export const renderStackedCloudNameAndIcon = (
  provider,
  isInline = false,
  accountId = '',
) => (
  <Stack direction={isInline ? 'row' : 'column'} spacing={isInline ? 2 : 0}>
    {
      <Box boxSize={5}>
        {provider ? getIcon(provider) : '-'} {accountId ? accountId : ''}
      </Box>
    }
    <Box>{getProviderName(provider)}</Box>
  </Stack>
);

export const renderCloudIcon = ({ provider, boxSize = 5 }) => (
  <Box boxSize={boxSize}>
    <CustomTooltip label={getProviderName(provider)}>
      {provider ? getIcon(provider) : '-'}
    </CustomTooltip>
  </Box>
);

export const cloudProviderOptions = [
  { label: 'AWS', value: CloudProviders.Aws, icon: <AwsIcon /> },
  { label: 'GCP', value: CloudProviders.Gcp, icon: <GoogleCloudIcon /> },
  { label: 'Azure', value: CloudProviders.Azure, icon: <AzureIcon /> },
  {
    label: 'Snowflake',
    value: CloudProviders.Snowflake,
    icon: <SnowflakeIcon />,
  },
  { label: 'GitLab', value: CloudProviders.Gitlab, icon: <GitLabIcon /> },
  { label: 'GitHub', value: CloudProviders.Github, icon: <GithubIcon /> },
  {
    label: 'Atlassian BitBucket',
    value: CloudProviders.Bitbucket,
    icon: <BitbucketIcon />,
  },
  {
    label: 'Okta',
    value: CloudProviders.Okta,
    icon: <OktaIcon />,
  },
];

export const cloudProviderOptionsWithAll = [
  { label: 'Cloud', value: undefined },
  ...cloudProviderOptions,
];

export const addCloudAccountOptions = [
  { label: 'Cloud', value: undefined },
  ...map(
    [
      CloudProviders.Aws,
      CloudProviders.Gcp,
      CloudProviders.Azure,
      CloudProviders.Gitlab,
      CloudProviders.Github,
      CloudProviders.Bitbucket,
    ],
    o => ({ label: getProviderName(o), value: o, icon: getIcon(o) }),
  ),
];

export const alertsCloudProviderOptionsWithAll = [
  { label: 'Cloud', value: undefined },
  ...map(
    [
      CloudProvider.Aws,
      CloudProvider.Gcp,
      CloudProvider.Azure,
      CloudProvider.Snowflake,
      CloudProvider.GitLab,
      CloudProvider.GitHub,
      CloudProvider.Bitbucket,
    ],
    o => ({ label: getProviderAlias(o), value: o, icon: getIcon(o) }),
  ),
];
